import { planToday } from "../../../shared/day.ts";

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000],
  ["month", 2_592_000],
  ["week", 604_800],
  ["day", 86_400],
  ["hour", 3_600],
  ["minute", 60],
];

/** "2h", "3d", "5m": how long ago, for a narrow column ("Replied 2h ago"). */
export function agoShort(iso: string | null | undefined): string {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  return s >= 86_400 ? `${Math.floor(s / 86_400)}d` : s >= 3_600 ? `${Math.floor(s / 3_600)}h` : `${Math.max(1, Math.floor(s / 60))}m`;
}

/** "3 hours ago", "yesterday", "just now". */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  for (const [unit, size] of UNITS) if (Math.abs(s) >= size) return rtf.format(Math.round(s / size), unit);
  return "just now";
}

const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const monthDayYear = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/**
 * The one way to show a day: "Nov 19", with the year only when it isn't this year ("Feb 6, 2027"). A date
 * (YYYY-MM-DD) is that day; a timestamp is the day it falls on at HHL.
 */
export function dayText(value: string | null | undefined): string {
  if (!value) return "";
  const at = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(at.getTime())) return value;
  const iso = value.length === 10 ? value : planToday(at);
  const d = new Date(`${iso}T12:00:00Z`);
  return (iso.slice(0, 4) === planToday().slice(0, 4) ? monthDay : monthDayYear).format(d);
}

/** Whole days from `from` to `to` (YYYY-MM-DD each). */
export const daysFrom = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export const VENDOR_KIND_LABELS = { supplier: "Supplier", contract_mfr: "Contract manufacturer", both: "Supplier + CM" } as const;
export const PROJECT_STATUS_LABELS = { npi: "New (NPI)", active: "Active", eol: "End of life", archived: "Archived" } as const;
export const ROLE_LABELS = { admin: "Admin", inventory: "Inventory", receiving: "Receiving" } as const;
/** What each role can do, in plain words (the sidebar footer). */
export const ROLE_PLAIN = {
  admin: "Admin · can change anything",
  inventory: "Inventory · buys, plans, receives",
  receiving: "Receiving · deliveries only",
} as const;

export const options = (labels: Record<string, string>) => Object.entries(labels).map(([value, label]) => ({ value, label }));
export const PROCUREMENT_LABELS = { buy: "Buy", make: "Make", both: "Buy or make" } as const;
export const LIFECYCLE_LABELS = { npi: "New (NPI)", active: "Active", eol: "End of life", obsolete: "Obsolete" } as const;
export const ADDRESS_KIND_LABELS = { billing: "Billing", ship_from: "Ships from", factory: "Factory", remit: "Remit to", other: "Other" } as const;

/** $4.00, $0.0985: cents always, more places only when a part costs fractions of a cent (1.5700000000000001 is $1.57). */
export function money(value: unknown, currency = "USD"): string {
  if (value == null || value === "") return "";
  const n = Math.round(Number(value) * 10_000) / 10_000;
  if (!Number.isFinite(n)) return "";
  const places = Math.min(4, Math.max(2, (String(n).split(".")[1] ?? "").length));
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: places, maximumFractionDigits: places }).format(n);
}

export const qty = (value: unknown) =>
  value == null || value === "" ? "" : new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(Number(value));
export const days = (value: unknown) => (value == null || value === "" ? "" : plural(Number(value), "day"));

/** "1 day", "3 days", "1,040 units". */
export const plural = (n: number, one: string, many = `${one}s`) => `${qty(n)} ${n === 1 ? one : many}`;

/** "+40" or "−265" (a true minus sign), for stock beside another number. */
export const signed = (n: number) => (n < 0 ? `−${qty(-n)}` : `+${qty(n)}`);

/** Who did something: their name (or email), "HHL Ops" for the app's own jobs and imports (no user). */
export function personName(users: readonly { id: string; name: string; email: string }[], id: string | null | undefined): string {
  if (!id) return "HHL Ops";
  const u = users.find((x) => x.id === id);
  return u ? u.name || u.email : "a former user";
}

/** A count typed in a box: null when it's empty, undefined when it isn't a number of 0 or more ("1,200" is 1200). */
export const typedCount = (text: string): number | null | undefined => {
  if (text === "") return null;
  const n = Number(text.replace(/[,\s]/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};
